/**
 * Device pixel ratio for canvas backing stores — capped to keep the combined
 * canvas + compositor memory within mobile renderer budgets.
 *
 * iOS Safari/WebKit (and Chrome on iOS, which is WebKit too) kills and reloads
 * the tab ("a problem repeatedly occurred" / "Can't open this page") when the
 * total canvas + layer memory is too high. That is pronounced here because the
 * fixed 1600×900 stage rasterises the UI at full design size, and the game also
 * runs two WebGL contexts plus several 2D canvases. At DPR 3 every canvas is 9×
 * the pixels of DPR 1; capping at 2 roughly halves that with no meaningful
 * sharpness loss for this game on a phone-sized display.
 *
 * PHONE CRASH LOOP: a DPR-2 cap fits an iPad's memory headroom, but an iPhone's
 * WebContent process sits in a far tighter jetsam budget — the same DPR-2
 * backing stores (the two full-viewport UI-chrome canvases + its WebGL bloom,
 * the arena game/webgl canvases + their framebuffers) push it over the limit, so
 * WebKit kills and reloads the web view. In the native Capacitor shell that
 * reload drops the player back to the boot screen, again and again (the iPad,
 * with the headroom, stays stable). On a phone-class screen the fixed stage is
 * scaled down to a small physical display anyway, so DPR 1 is ~the physical
 * pixel need — capping there quarters every backing store (½ width × ½ height)
 * and keeps the app inside the budget with no visible softness.
 *
 * IMPORTANT: every place that sizes the SAME canvas (CanvasScaler sizing it,
 * renderPipeline reading it back to derive logical dims) must use this single
 * source, or the dpr mismatch corrupts the logical-coordinate math.
 *
 * The phone-class test (short edge < 600 CSS px) is shared with the whole UX
 * layer via {@link isCompactScreen} in `utils/deviceTier` — one signal drives
 * both the jetsam DPR cap here and the phone chrome branch.
 */
import { isCompactScreen } from "./deviceTier.ts";

export function displayDpr(): number {
  const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
  const cap = isCompactScreen() ? 1 : 2;
  return Math.min(Math.max(1, dpr), cap);
}
